import { getStoredToken } from "../auth/session";
import api, { refreshCurrentSession } from "./api";

const MIN_RECONNECT_DELAY_MS = 500;
const MAX_RECONNECT_DELAY_MS = 5000;
const HEARTBEAT_TIMEOUT_MS = 30000;

const parseSseFrame = (rawFrame) => {
  const frame = { data: "" };
  const dataLines = [];

  rawFrame.split(/\r?\n/).forEach((line) => {
    if (!line || line.startsWith(":")) return;

    const separator = line.indexOf(":");
    const field = separator < 0 ? line : line.slice(0, separator);
    const value = separator < 0 ? "" : line.slice(separator + 1).trimStart();

    if (field === "data") dataLines.push(value);
    if (field === "id") frame.id = value;
    if (field === "event") frame.type = value;
    if (field === "retry" && /^\d+$/.test(value)) frame.retry = Number(value);
  });

  if (!dataLines.length) return null;

  const data = dataLines.join("\n");
  try {
    frame.data = JSON.parse(data);
  } catch {
    return null;
  }

  return frame;
};

const buildStreamUrl = (batchId) => {
  const baseUrl = String(api.defaults.baseURL || "").replace(/\/$/, "");
  return `${baseUrl}/upload/event-photos/batches/${encodeURIComponent(batchId)}/events`;
};

const getDelay = (attempt, serverRetry) => {
  if (serverRetry) {
    return Math.max(MIN_RECONNECT_DELAY_MS, Math.min(serverRetry, MAX_RECONNECT_DELAY_MS));
  }

  return Math.min(
    MIN_RECONNECT_DELAY_MS * 2 ** Math.max(0, attempt - 1),
    MAX_RECONNECT_DELAY_MS,
  );
};

export const subscribeToUploadBatchEvents = ({
  batchId,
  onOpen = () => undefined,
  onEvent = () => undefined,
  onReconnect = () => undefined,
  onAuthorizationError = () => undefined,
  onError = () => undefined,
  fetchImpl = (...args) => window.fetch(...args),
  refreshSession = refreshCurrentSession,
  retryDelay = getDelay,
}) => {
  let stopped = false;
  let reader;
  let controller;
  let reconnectTimer;
  let heartbeatTimer;
  let lastEventId = "";
  let reconnectAttempt = 0;
  let serverRetry;

  const stop = () => {
    stopped = true;
    window.clearTimeout(reconnectTimer);
    window.clearTimeout(heartbeatTimer);
    controller?.abort();
    reader?.cancel().catch(() => undefined);
  };

  const scheduleReconnect = (connect) => {
    if (stopped || reconnectTimer) return;

    window.clearTimeout(heartbeatTimer);
    reconnectAttempt += 1;
    const delay = retryDelay(reconnectAttempt, serverRetry);
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = undefined;
      if (stopped) return;
      onReconnect({ attempt: reconnectAttempt, delay, lastEventId });
      void connect();
    }, delay);
  };

  const resetHeartbeatTimer = (connect) => {
    window.clearTimeout(heartbeatTimer);
    heartbeatTimer = window.setTimeout(() => {
      heartbeatTimer = undefined;
      if (stopped) return;
      controller?.abort();
      reader?.cancel().catch(() => undefined);
      scheduleReconnect(connect);
    }, HEARTBEAT_TIMEOUT_MS);
  };

  const connect = async () => {
    const token = getStoredToken();
    if (!token) {
      onAuthorizationError({ status: 401 });
      return;
    }

    controller = new AbortController();
    try {
      const response = await fetchImpl(buildStreamUrl(batchId), {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${token}`,
          ...(lastEventId ? { "Last-Event-ID": lastEventId } : {}),
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          if (await refreshSession()) {
            scheduleReconnect(connect);
            return;
          }

          onAuthorizationError({ status: 401 });
          return;
        }
        if (response.status === 403) {
          onAuthorizationError({ status: response.status });
          return;
        }
        throw new Error(`SSE indisponivel (${response.status})`);
      }
      if (!response.body) throw new Error("SSE sem corpo de resposta");

      onOpen({ reconnected: reconnectAttempt > 0, lastEventId });
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      resetHeartbeatTimer(connect);

      while (!stopped) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
        const frames = buffer.split(/\r?\n\r?\n/);
        buffer = frames.pop() || "";

        for (const rawFrame of frames) {
          const event = parseSseFrame(rawFrame);
          if (!event) continue;

          if (event.id) lastEventId = event.id;
          if (event.retry) serverRetry = event.retry;
          if (
            event.type === "snapshot.required" &&
            /^\d+$/.test(String(event.data?.latestEventId || ""))
          ) {
            lastEventId = String(event.data.latestEventId);
          }

          reconnectAttempt = 0;
          resetHeartbeatTimer(connect);
          onEvent(event);
        }

        if (done) break;
      }

      if (!stopped) scheduleReconnect(connect);
    } catch (error) {
      if (stopped || error?.name === "AbortError") return;
      onError(error);
      scheduleReconnect(connect);
    }
  };

  void connect();
  return stop;
};
